// Small presentational building blocks shared by the dashboard tabs.
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Info,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Line, LineChart, ResponsiveContainer, YAxis } from "recharts";
import { SENTIMENT_BY_KEY } from "./constants";
import { exportElement, nf, pct } from "./utils";

export const Stars = ({ value, size = "md" }) => (
  <span
    className={`gr-stars gr-stars-${size}`}
    role="img"
    aria-label={`Ocena ${nf(value, 1)} na 5`}
  >
    <span className="gr-stars-base">★★★★★</span>
    <span
      className="gr-stars-fill"
      style={{ width: `${(Math.max(0, Math.min(5, value || 0)) / 5) * 100}%` }}
    >
      ★★★★★
    </span>
  </span>
);

export const Delta = ({
  current,
  previous,
  kind = "number",
  digits = 1,
  goodWhen = "up",
  title,
}) => {
  if (
    previous === null ||
    previous === undefined ||
    current === null ||
    current === undefined
  )
    return null;
  let diff = current - previous;
  let text;
  if (kind === "relative") {
    if (!previous)
      return (
        <span className="gr-delta gr-delta-flat" title={title}>
          nowe
        </span>
      );
    diff = ((current - previous) / previous) * 100;
    text = `${nf(Math.abs(diff), 0)}%`;
  } else if (kind === "pp") {
    text = `${nf(Math.abs(diff), digits)} pp`;
  } else {
    text = nf(Math.abs(diff), digits);
  }
  const threshold = kind === "number" ? 0.5 * 10 ** -digits : 0.05;
  if (Math.abs(diff) < threshold) {
    return (
      <span className="gr-delta gr-delta-flat" title={title}>
        bez zmian
      </span>
    );
  }
  const up = diff > 0;
  const good = goodWhen === "up" ? up : !up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={`gr-delta ${good ? "gr-delta-good" : "gr-delta-bad"}`}
      title={title}
    >
      <Icon size={14} aria-hidden="true" />
      {up ? "+" : "−"}
      {text}
    </span>
  );
};

export const Sparkline = ({ data, dataKey, color }) => {
  const points = (data || []).filter(
    (d) => d[dataKey] !== null && d[dataKey] !== undefined,
  );
  if (points.length < 2) return null;
  return (
    <div className="gr-sparkline" aria-hidden="true">
      <ResponsiveContainer width="100%" height={36}>
        <LineChart
          data={data}
          margin={{ top: 4, right: 2, bottom: 2, left: 2 }}
        >
          <YAxis hide domain={["dataMin", "dataMax"]} />
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const KpiCard = ({
  label,
  value,
  suffix,
  hint,
  delta,
  children,
  spark,
  tone,
}) => (
  <div className={`gr-kpi ${tone ? `gr-kpi-${tone}` : ""}`}>
    <div className="gr-kpi-label">{label}</div>
    <div className="gr-kpi-main">
      <span className="gr-kpi-value">{value}</span>
      {suffix && <span className="gr-kpi-suffix">{suffix}</span>}
    </div>
    {children}
    <div className="gr-kpi-foot">
      {delta}
      {hint && <span className="gr-kpi-hint">{hint}</span>}
    </div>
    {spark}
  </div>
);

export const MiniStat = ({ label, value, hint, delta }) => (
  <div className="gr-ministat">
    <span className="gr-ministat-label">{label}</span>
    <span className="gr-ministat-value">{value}</span>
    <span className="gr-ministat-hint">
      {delta}
      {hint}
    </span>
  </div>
);

export const Card = ({
  id,
  title,
  subtitle,
  actions,
  children,
  className = "",
  exportName,
}) => (
  <section
    className={`gr-card ${className}`}
    id={id}
    aria-labelledby={id ? `${id}-title` : undefined}
  >
    {(title || actions || exportName) && (
      <header className="gr-card-header">
        <div className="gr-card-titles">
          {title && <h3 id={id ? `${id}-title` : undefined}>{title}</h3>}
          {subtitle && <p>{subtitle}</p>}
        </div>
        <div className="gr-card-actions" data-export-ignore="true">
          {actions}
          {exportName && id && (
            <button
              type="button"
              className="gr-icon-btn"
              onClick={() => exportElement(id, exportName)}
              title="Eksportuj jako PNG"
              aria-label="Eksportuj jako PNG"
            >
              <Download size={16} />
            </button>
          )}
        </div>
      </header>
    )}
    {children}
  </section>
);

export const EmptyState = ({ title, text, action }) => (
  <div className="gr-empty">
    <Info size={22} aria-hidden="true" />
    <strong>{title}</strong>
    {text && <p>{text}</p>}
    {action}
  </div>
);

export const SentimentBar = ({ positive, neutral, negative, showLegend = false }) => {
  const total = positive + neutral + negative;
  const parts = [
    { key: "pozytywna", value: positive },
    { key: "neutralna", value: neutral },
    { key: "negatywna", value: negative },
  ];
  return (
    <div className="gr-sentbar-wrap">
      <div
        className="gr-sentbar"
        role="img"
        aria-label={`Pozytywne ${positive}, neutralne ${neutral}, negatywne ${negative}`}
      >
        {parts.map(
          (p) =>
            p.value > 0 && (
              <span
                key={p.key}
                style={{
                  width: `${pct(p.value, total)}%`,
                  background: SENTIMENT_BY_KEY[p.key].color,
                }}
                title={`${SENTIMENT_BY_KEY[p.key].label}: ${p.value} (${nf(pct(p.value, total), 1)}%)`}
              />
            ),
        )}
      </div>
      {showLegend && (
        <div className="gr-sentbar-legend">
          {parts.map((p) => (
            <span key={p.key}>
              <i style={{ background: SENTIMENT_BY_KEY[p.key].color }} />
              {nf(pct(p.value, total), 0)}%
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Horizontal bar list: readable labels on every screen size, every row is a drill-down button.
export const BarList = ({
  items,
  color,
  onSelect,
  valueLabel = "wzmianek",
  emptyText = "Brak danych",
  limit,
}) => {
  const [expanded, setExpanded] = useState(false);
  if (!items.length) return <EmptyState title={emptyText} />;
  const max = Math.max(...items.map((i) => i.value), 1);
  const visible = limit && !expanded ? items.slice(0, limit) : items;
  return (
    <>
      <ul className="gr-barlist">
        {visible.map((item) => (
          <li key={item.key}>
            <button
              type="button"
              onClick={() => onSelect && onSelect(item)}
              disabled={!onSelect}
              title={item.title}
            >
              <span className="gr-barlist-head">
                <span className="gr-barlist-label">{item.label}</span>
                <span className="gr-barlist-value">
                  {nf(item.value)} <small>{valueLabel}</small>
                </span>
              </span>
              <span className="gr-barlist-track">
                <span
                  className="gr-barlist-fill"
                  style={{
                    width: `${(item.value / max) * 100}%`,
                    background: item.color || color,
                  }}
                />
              </span>
              {item.secondary && (
                <span className="gr-barlist-secondary">{item.secondary}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
      {limit && items.length > limit && (
        <button
          type="button"
          className="gr-link-btn"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? "Pokaż mniej" : `Pokaż wszystkie (${items.length})`}
          <ChevronDown size={14} className={expanded ? "gr-rotate" : ""} />
        </button>
      )}
    </>
  );
};

export const ChartTooltip = ({ active, payload, label, render }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="gr-tooltip">
      {render(payload[0].payload, label, payload)}
    </div>
  );
};

export const TooltipRow = ({ color, label, value }) => (
  <div className="gr-tooltip-row">
    <span>
      {color && <i style={{ background: color }} />}
      {label}
    </span>
    <strong>{value}</strong>
  </div>
);

export const Segmented = ({ options, value, onChange, label }) => (
  <div className="gr-segmented" role="group" aria-label={label}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        className={value === o.value ? "is-active" : ""}
        aria-pressed={value === o.value}
        onClick={() => onChange(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const Pagination = ({ page, pages, onChange }) => {
  if (pages <= 1) return null;
  const numbers = [];
  const from = Math.max(1, page - 2);
  const to = Math.min(pages, page + 2);
  if (from > 1) numbers.push(1);
  if (from > 2) numbers.push("…l");
  for (let i = from; i <= to; i += 1) numbers.push(i);
  if (to < pages - 1) numbers.push("…r");
  if (to < pages) numbers.push(pages);
  return (
    <nav className="gr-pagination" aria-label="Strony wyników">
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        aria-label="Poprzednia strona"
      >
        <ChevronLeft size={16} />
      </button>
      {numbers.map((n) =>
        typeof n === "number" ? (
          <button
            type="button"
            key={n}
            className={n === page ? "is-active" : ""}
            aria-current={n === page ? "page" : undefined}
            onClick={() => onChange(n)}
          >
            {n}
          </button>
        ) : (
          <span key={n} className="gr-pagination-gap">
            …
          </span>
        ),
      )}
      <span className="gr-pagination-compact">
        {page} / {pages}
      </span>
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page >= pages}
        aria-label="Następna strona"
      >
        <ChevronRight size={16} />
      </button>
    </nav>
  );
};

export const SortableTable = ({
  columns,
  rows,
  initialSort,
  onRowClick,
  rowKey,
  pageSize = 10,
  emptyText,
}) => {
  const [sort, setSort] = useState(initialSort);
  const [limit, setLimit] = useState(pageSize);
  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const value = col.sortValue || ((r) => r[col.key]);
    return [...rows].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va === vb) return 0;
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      const cmp = typeof va === "string" ? va.localeCompare(vb, "pl") : va - vb;
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, columns, sort]);

  if (!rows.length) return <EmptyState title={emptyText || "Brak danych"} />;
  const toggle = (key) =>
    setSort((s) => ({
      key,
      dir: s.key === key && s.dir === "desc" ? "asc" : "desc",
    }));

  return (
    <>
      <div className="gr-table-wrap">
        <table className="gr-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={c.align === "right" ? "is-num" : ""}
                  aria-sort={
                    sort.key === c.key
                      ? sort.dir === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  {c.sortable === false ? (
                    c.label
                  ) : (
                    <button type="button" onClick={() => toggle(c.key)}>
                      {c.label}
                      {sort.key === c.key && (
                        <ChevronDown
                          size={12}
                          className={sort.dir === "asc" ? "gr-rotate" : ""}
                        />
                      )}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.slice(0, limit).map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? "is-clickable" : ""}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === "Enter") onRowClick(row);
                      }
                    : undefined
                }
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-label={c.label}
                    className={`${c.align === "right" ? "is-num" : ""} ${c.primary ? "is-primary" : ""}`}
                  >
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sorted.length > limit && (
        <button
          type="button"
          className="gr-link-btn"
          onClick={() => setLimit((l) => l + pageSize)}
        >
          Pokaż więcej ({sorted.length - limit}) <ChevronDown size={14} />
        </button>
      )}
    </>
  );
};

export const RatingCell = ({ value }) => (
  <span
    className="gr-rating-cell"
    style={{
      color: value < 3.5 ? "#c0392b" : value < 4.5 ? "#8a5a00" : "inherit",
    }}
  >
    {nf(value, 2)} ★
  </span>
);
